import React, { useEffect, useState } from 'react'
import { WebsitePreviewContent } from './previews/WebsitePreviewContent'
import { getApiBase } from '../../utils/api'
import { getQRBranding } from '../../utils/branding'
import { Loader } from 'lucide-react'

interface WebsiteProductionWrapperProps {
  linkId?: string
  linkData?: any
  isPreview?: boolean
  accountSlug?: string
}

export const WebsiteProductionWrapper: React.FC<WebsiteProductionWrapperProps> = ({
  linkId,
  linkData: propLinkData,
  isPreview = false,
  accountSlug
}) => {
  const [linkData, setLinkData] = useState<any>(propLinkData || null)
  const [branding, setBranding] = useState<any>(null)
  const [loading, setLoading] = useState(!propLinkData)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!linkId || propLinkData) return
      try {
        setLoading(true)
        const API_BASE = getApiBase()
        const endpoint = accountSlug
          ? `${API_BASE}/api/public/link/${accountSlug}/${linkId}`
          : `${API_BASE}/api/public/link/${linkId}`
        const response = await fetch(endpoint)
        if (!response.ok) throw new Error('Link not found')
        const data = await response.json()
        setLinkData(data)
        const brandingConfig = await getQRBranding(data.branding_type, data.author_id)
        setBranding(brandingConfig)
      } catch (err) {
        console.error('Error loading Website data:', err)
        setError('Website konnte nicht geladen werden')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [linkId, propLinkData, accountSlug])

  // Load branding for preview mode
  useEffect(() => {
    const loadBranding = async () => {
      if (!propLinkData || branding) return
      try {
        const brandingConfig = await getQRBranding(
          propLinkData.branding_type || 'beyondpages',
          propLinkData.author_id
        )
        setBranding(brandingConfig)
      } catch (e) {
        console.error('Error loading website branding:', e)
      }
    }
    loadBranding()
  }, [propLinkData, branding])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error || !linkData) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">{error || 'Keine Daten verfügbar'}</p>
      </div>
    )
  }

  // Transform data for WebsitePreviewContent
  const previewData = {
    title: linkData.title || '',
    description: linkData.metadata?.description || linkData.description || '',
    target_url: linkData.target_url || '',
    favicon: linkData.metadata?.favicon || '',
    buttonText: linkData.metadata?.buttonText || '',
    fonts: linkData.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
    textSizes: linkData.metadata?.textSizes || {},
    companyPosition: linkData.metadata?.companyPosition || 'bottom',
    customColors: linkData.metadata?.customColors || undefined
  } as any

  const brandingData = {
    companyName: branding?.companyName || '',
    brandingType: linkData.branding_type || 'beyondpages',
    textColor: linkData.metadata?.customColors?.textColor || branding?.textColor || '#FFFFFF',
    primaryColor: linkData.metadata?.customColors?.primaryColor || branding?.primaryColor || '#3B82F6',
    secondaryColor: linkData.metadata?.customColors?.secondaryColor || branding?.secondaryColor || '#1F2937',
  }

  const colorMode = linkData.metadata?.colorMode || 'gradient'
  const bgGradient = `linear-gradient(to bottom right, ${brandingData.primaryColor}, ${brandingData.secondaryColor})`

  return (
    <WebsitePreviewContent 
      data={previewData}
      branding={brandingData}
      bgGradient={bgGradient}
      colorMode={colorMode}
      isPreview={isPreview}
    />
  )
}

